import moment from 'moment/moment'

import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Lesson } from './Lesson'
import { FKType } from './SQLBuilder'
import { Staffing, StaffingConstructorArgs } from './Staffing'

type AvailabilityConstructorArgs = Omit<
    ConstructorArgs<() => Availability>,
    'staffing' | 'resources'
> & {
    staffing: Staffing[] | StaffingConstructorArgs[] | null
    resources: Staffing[] | StaffingConstructorArgs[] | null
}

export class Availability extends BaseRecord {
    readonly recordType = 'availability'

    start: moment.Moment | undefined
    end: moment.Moment | undefined
    checkinDate: moment.Moment | undefined
    checkoutDate: moment.Moment | undefined
    available: boolean | undefined
    capacity: number | undefined
    waiting: number | undefined
    actual: number | undefined
    numberOfSlots: number | undefined
    date: moment.Moment | undefined
    annotation: string | undefined
    staffing: Staffing[] = []
    resources: Staffing[] = []

    constructor(data?: Partial<AvailabilityConstructorArgs>) {
        super(data)
        if (!data) {
            return
        }

        this.start = this.propertyFromTimestampArg(data.start)
        this.end = this.propertyFromTimestampArg(data.end)
        this.checkinDate = this.propertyFromTimestampArg(data.checkin_date)
        this.checkoutDate = this.propertyFromTimestampArg(data.checkout_date)
        this.available = data.available ?? false
        this.capacity = this.propertyFromNumberArg(data.capacity)
        this.waiting = this.propertyFromNumberArg(data.waiting)
        this.actual = this.propertyFromNumberArg(data.actual)
        this.numberOfSlots = this.propertyFromNumberArg(data.number_of_slots)
        this.date = this.propertyFromDateArg(data.date)
        this.annotation = data.annotation ?? undefined
        this.staffing = (data.staffing ?? []).map((staffing) =>
            staffing instanceof Staffing ? staffing : new Staffing(staffing)
        )
        this.resources = (data.resources ?? []).map((staffing) =>
            staffing instanceof Staffing ? staffing : new Staffing(staffing)
        )
    }

    clone(overrides?: Partial<AvailabilityConstructorArgs>): Availability {
        return new Availability({ ...this.serializeForClone(), ...overrides })
    }

    get title(): string {
        const timeString = moment(this.start).format('h:mma').toLowerCase()
        if (!timeString) return ''
        return timeString.substring(0, timeString.length - 1)
    }

    static dataType(): DataType {
        return 'availabilities'
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.start) this.start = moment(data.start)
        if (data.end) this.end = moment(data.end)
        if (data.lesson) this.lesson = BaseRecord.fromSerializer(data.lesson, Lesson)
        if (data.checkinDate) this.checkinDate = moment(data.checkinDate)
        if (data.checkoutDate) this.checkoutDate = moment(data.checkoutDate)
        if (data.date) this.date = moment(data.date)
        if (data.staffing) {
            this.staffing = data.staffing.map((staffing: any) =>
                BaseRecord.fromSerializer(staffing, Staffing)
            )
        }
        if (data.resources) {
            this.resources = data.resources.map((resource: any) =>
                BaseRecord.fromSerializer(resource, Staffing)
            )
        }
    }

    static fk: FKType = {
        lessons: ['lesson'],
    }

    get lesson(): Lesson | undefined {
        return this.getForeignRecord('lesson', Lesson)
    }
    set lesson(lesson: Lesson | undefined) {
        this.setForeignRecord('lesson', lesson)
    }
}
